import { type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/shared/utils/cn';

const avatarVariants = cva(
  'inline-flex items-center justify-center rounded-full font-semibold uppercase',
  {
    variants: {
      size: {
        xs: 'h-6 w-6 text-[10px]',
        sm: 'h-8 w-8 text-xs',
        md: 'h-10 w-10 text-sm',
        lg: 'h-12 w-12 text-base',
        xl: 'h-16 w-16 text-lg',
      },
      variant: {
        primary: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300',
        secondary: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
        success: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
        warning: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
        danger: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
        info: 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300',
      },
    },
    defaultVariants: {
      size: 'md',
      variant: 'primary',
    },
  }
);

export interface AvatarProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof avatarVariants> {
  name?: string;
  src?: string;
  alt?: string;
}

function getInitials(name: string): string {
  if (!name) return '?';

  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getEmailInitials(email: string): string {
  if (!email) return '?';

  const localPart = email.split('@')[0];
  if (!localPart) return '?';

  if (localPart.includes('.')) {
    const parts = localPart.split('.');
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  return localPart.substring(0, 2).toUpperCase();
}

export function Avatar({
  name,
  src,
  alt,
  size,
  variant,
  className,
  ...props
}: AvatarProps) {
  const initials = name?.includes('@') ? getEmailInitials(name) : getInitials(name || '');

  if (src) {
    return (
      <img
        src={src}
        alt={alt || name || 'Avatar'}
        loading="lazy"
        className={cn(avatarVariants({ size }), 'object-cover', className)}
        {...props}
      />
    );
  }

  return (
    <div className={cn(avatarVariants({ size, variant }), className)} {...props}>
      {initials}
    </div>
  );
}

const orgTypeColors: Record<string, VariantProps<typeof avatarVariants>['variant']> = {
  licensee: 'primary',
  licensor: 'success',
  retailer: 'warning',
  agent: 'info',
};

export interface OrgAvatarProps extends Omit<AvatarProps, 'variant'> {
  orgType?: string;
}

export function OrgAvatar({ orgType, ...props }: OrgAvatarProps) {
  const variant = orgTypeColors[orgType || ''] || 'secondary';
  return <Avatar variant={variant} {...props} />;
}

export { avatarVariants };
